import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Snackbar, Slide, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useAuth, useUnifiedProjects, vortxColors, retroColors, vortxTypography } from '@vortx/shared-ui';
import { useRetroContext } from '@vortx/shared-ui';
import {
    Dashboard as DashboardIcon,
    Folder as FolderIcon,
    Monitor as MonitorIcon,
    ElectricCar as EVIcon,
    LocationOn,
    Settings,
    ElectricBolt,
    Build,
    AttachMoney,
    Assessment,
    Analytics,
    ArrowBack,
    Business as CompanyIcon,
    Groups as TeamIcon,
    CreditCard as BillingIcon,
    Construction as EquipmentIcon,
    ShowChart as LoadProfileIcon,
    AccountBalance as BudgetIcon,
    Domain as AllCompaniesIcon,
    People as AllUsersIcon,
    Memory as SolverIcon,
    Bolt as DerChargesIcon,
    BugReport as BugIcon,
    Visibility as ActivityIcon,
    LocalShipping as FleetIcon,
    DataUsage as DataUsageIcon,
    AcUnit as ColdStorageIcon,
    SolarPower as SolarPowerIcon,
    Science as ScienceIcon,
    MenuBook as WikiIcon,
    AddCircleOutline as AddCircleIcon,
    ViewInAr as ViewInArIcon,
    ElectricalServices as AcDcIcon,
    MonetizationOn as VppIcon,
    EvStation as EvStationIcon,
    Schedule as ScheduleIcon,
    Tune as TuneIcon,
    Psychology as LearningIcon,
} from '@mui/icons-material';

const PRIMARY_NAV_ITEMS = [
    { id: 'dashboard', label: 'Mission Control', path: '/dashboard', icon: DashboardIcon, color: '#b28c50', requiresProduct: null },
    { id: 'all-projects', label: 'Projects', path: '/all-projects', icon: FolderIcon, color: '#b28c50', requiresProduct: null },
    { id: 'flux-phd', label: 'Flux PhD', path: '/phd', icon: ScienceIcon, color: '#9b59b6', requiresProduct: 'flux_phd' },
];

const APPS_NAV_ITEMS = [
    { id: 'site-dna', label: 'Site DNA', path: '/projects', icon: FolderIcon, color: '#b28c50', requiresProduct: null },
    { id: 'flux-ev', label: 'Flux EV', path: '/ev', icon: EVIcon, color: '#4CAF50', requiresProduct: null },
    { id: 'cold-storage', label: 'Flux COLD', path: '/cold-storage', icon: ColdStorageIcon, color: '#2196F3', requiresProduct: null },
    { id: 'flux-ems', label: 'Flux EMS', path: '/ems', icon: MonitorIcon, color: '#5fd4c4', requiresProduct: 'flux_ems' },
    { id: 'flux-dc', label: 'Flux DC', path: '/dc', icon: SolverIcon, color: '#ff9800', requiresProduct: null },
];

const SCENARIO_TAB_ITEMS = [
    { index: 0, label: 'Site', icon: LocationOn },
    { index: 1, label: 'Utility', icon: Settings },
    { index: 2, label: 'Load', icon: ElectricBolt },
    { index: 3, label: 'Design', icon: Build },
    { index: 4, label: 'Budget/Terms', icon: AttachMoney },
    { index: 5, label: 'Variants', icon: Assessment },
    { index: 6, label: 'Results', icon: Analytics },
];

// Flux PhD navigation items (platform-level, project-independent)
const FLUX_PHD_NAV_ITEMS = [
    { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
    { id: 'forecasting', label: 'Forecasting', icon: ScienceIcon },
    { id: 'research', label: 'Research', icon: Analytics },
    { id: 'learning', label: 'Learning', icon: LearningIcon },
    { id: 'calibration', label: 'Site Calibration', icon: TuneIcon },
    { id: 'wiki', label: 'Wiki', icon: WikiIcon },
];

// Flux EV navigation items — project-level
const FLUX_EV_NAV_ITEMS = [
    { id: 'scenarios', label: 'Scenarios', icon: Assessment },
    { id: 'vpp', label: 'VPP Revenue', icon: VppIcon },
];

// Flux EV scenario-level tabs (inside a scenario editor)
const FLUX_EV_SCENARIO_TAB_ITEMS = [
    { index: 0, label: 'Fleet', icon: FleetIcon },
    { index: 1, label: 'Chargers', icon: EvStationIcon },
    { index: 2, label: 'Utility', icon: ElectricBolt },
    { index: 3, label: 'Schedule', icon: ScheduleIcon },
    { index: 4, label: 'Results', icon: Analytics },
    { index: 5, label: 'Financial', icon: AttachMoney },
];

// Depot project navigation items
const DEPOT_NAV_ITEMS = [
    { id: 'upload', label: 'Upload', icon: DataUsageIcon },
    { id: 'results', label: 'Results', icon: Analytics },
    { id: 'vpp', label: 'VPP Revenue', icon: VppIcon },
];

// Flux COLD navigation items - project-level
const FLUX_COLD_NAV_ITEMS = [
    { id: 'zones', label: 'Facility', icon: ColdStorageIcon },
    { id: 'results', label: 'Results', icon: Analytics },
];

// Project overview navigation items
const PROJECT_OVERVIEW_NAV_ITEMS = [
    { id: 'overview', label: 'Overview', icon: DashboardIcon },
    { id: 'meters', label: 'Meters & Tariffs', icon: ElectricBolt },
];

// Flux EV standalone tools (not tied to project data)
// Removed depot - now integrated as project type
const FLUX_EV_TOOL_ITEMS = [];

// Admin navigation items
const ADMIN_ACCOUNT_ITEMS = [
    { id: 'admin-dashboard', label: 'Dashboard', path: '/admin', icon: DashboardIcon },
    { id: 'admin-activity', label: 'Activity Monitor', path: '/admin/activity', icon: ActivityIcon },
    { id: 'admin-company', label: 'Company', path: '/admin/company', icon: CompanyIcon },
    { id: 'admin-team', label: 'Team & Seats', path: '/admin/team', icon: TeamIcon },
    { id: 'admin-billing', label: 'Billing', path: '/admin/billing', icon: BillingIcon },
];

const ADMIN_PRODUCT_ITEMS = [
    { id: 'admin-tariffs', label: 'Tariffs', path: '/admin/tariffs', icon: ElectricBolt },
    { id: 'admin-capex', label: 'CapEx & OpEx', path: '/admin/capex', icon: AttachMoney },
    { id: 'admin-equipment', label: 'Equipment', path: '/admin/equipment', icon: EquipmentIcon },
    { id: 'admin-load-profiles', label: 'Load Profiles', path: '/admin/load-profiles', icon: LoadProfileIcon },
    { id: 'admin-budgets', label: 'Budgets', path: '/admin/budgets', icon: BudgetIcon },
    { id: 'admin-ev-vehicles', label: 'EV Vehicles', path: '/admin/ev-vehicles', icon: EVIcon },
];

const ADMIN_PLATFORM_ITEMS = [
    { id: 'admin-companies', label: 'All Companies', path: '/admin/companies', icon: AllCompaniesIcon },
    { id: 'admin-users', label: 'All Users', path: '/admin/users', icon: AllUsersIcon },
    { id: 'admin-solver', label: 'Solver Monitor', path: '/admin/solver', icon: SolverIcon },
    { id: 'admin-der-charges', label: 'CA DER Charges', path: '/admin/der-charges', icon: DerChargesIcon },
    { id: 'admin-bugs', label: 'Bug Reports', path: '/admin/bugs', icon: BugIcon },
];

// Mapping from unified project app keys to nav config
const APP_NAV_MAP = {
    site_dna: { id: 'site-dna', label: 'Site DNA', icon: SolarPowerIcon, color: '#b28c50' },
    flux_ev: { id: 'flux-ev', label: 'Flux EV', icon: EVIcon, color: '#4CAF50' },
    flux_cold: { id: 'cold-storage', label: 'Flux COLD', icon: ColdStorageIcon, color: '#2196F3' },
    flux_ems: { id: 'flux-ems', label: 'Flux EMS', icon: MonitorIcon, color: '#5fd4c4' },
    flux_dc: { id: 'flux-dc', label: 'Flux DC', icon: SolverIcon, color: '#ff9800' },
};
const ALL_APP_KEYS = ['site_dna', 'flux_ev', 'flux_cold', 'flux_ems', 'flux_dc'];

const buildProjectRoute = (appKey, projectId) => {
    switch (appKey) {
        case 'site_dna': return `/projects/${projectId}/analysis`;
        case 'flux_ev': return `/ev/${projectId}/site`;
        case 'flux_cold': return `/cold-storage/${projectId}`;
        case 'flux_ems': return `/ems/${projectId}`;
        default: return '/all-projects';
    }
};

// Dimmed nav item for disabled apps in project scope
const DimmedNavItem = ({ icon: Icon, label, color = '#b28c50', onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            borderLeft: '2px solid',
            borderLeftColor: `${color}20`,
            background: 'transparent',
            transition: 'all 0.2s ease',
            opacity: 0.35,
            '&:hover': {
                opacity: 0.6,
                borderLeftColor: `${color}60`,
                background: `${color}08`,
            },
        }}
    >
        <Box sx={{
            color: `${color}60`,
            transition: 'all 0.2s ease',
        }}>
            {React.isValidElement(Icon) ? Icon : <Icon sx={{ fontSize: 16 }} />}
        </Box>
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
            color: retroColors.dimmed,
            fontWeight: 400,
        }}>
            {label}
        </Typography>
        <AddCircleIcon sx={{ fontSize: 12, color: `${color}40`, ml: 'auto' }} />
    </Box>
);

const NavSection = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.6rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            color: retroColors.dimmed,
            px: 1,
            mb: 1,
        }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const NavItem = ({ icon: Icon, label, isActive, onClick, color = '#b28c50', href }) => {
    const handleClick = (e) => {
        // Allow ctrl+click, cmd+click, or middle-click to open in new tab
        if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let browser handle it
        }
        e.preventDefault();
        onClick?.();
    };

    return (
        <Box
            component={href ? 'a' : 'div'}
            href={href}
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                borderLeft: '2px solid',
                borderLeftColor: isActive ? color : `${color}40`,
                background: isActive ? `${color}20` : 'transparent',
                transition: 'all 0.2s ease',
                position: 'relative',
                textDecoration: 'none',
                '&:hover': {
                    borderLeftColor: color,
                    background: `${color}15`,
                },
                // Glow bar - always visible, brighter when active
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '2px',
                    height: isActive ? '70%' : '40%',
                    background: color,
                    boxShadow: isActive
                        ? `0 0 8px ${color}, 0 0 14px ${color}80`
                        : `0 0 4px ${color}60`,
                    borderRadius: '0 2px 2px 0',
                    opacity: isActive ? 1 : 0.5,
                    transition: 'all 0.2s ease',
                },
                '&:hover::after': {
                    height: '60%',
                    opacity: 0.8,
                    boxShadow: `0 0 6px ${color}, 0 0 10px ${color}60`,
                },
            }}
        >
            <Box sx={{
                color: isActive ? color : `${color}90`,
                transition: 'all 0.2s ease',
                filter: isActive ? `drop-shadow(0 0 4px ${color})` : `drop-shadow(0 0 2px ${color}40)`,
            }}>
                {React.isValidElement(Icon) ? Icon : <Icon sx={{ fontSize: 16 }} />}
            </Box>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                color: isActive ? '#f4f1ec' : '#aaa',
                fontWeight: isActive ? 600 : 400,
                textShadow: isActive ? `0 0 8px ${color}60` : `0 0 4px ${color}20`,
                transition: 'all 0.2s ease',
            }}>
                {label}
            </Typography>
        </Box>
    );
};

// Back button for navigation
const BackButton = ({ onClick, label = 'Back', href }) => {
    const handleClick = (e) => {
        // Allow ctrl+click, cmd+click, or middle-click to open in new tab
        if (e.ctrlKey || e.metaKey || e.button === 1) {
            return;
        }
        e.preventDefault();
        onClick?.();
    };

    return (
        <Box
            component={href ? 'a' : 'div'}
            href={href}
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                borderLeft: `2px solid ${retroColors.accent}66`,
                background: 'transparent',
                transition: 'all 0.2s ease',
                textDecoration: 'none',
                mb: 2,
                '&:hover': {
                    borderLeftColor: retroColors.accent,
                    background: `${retroColors.accent}1a`,
                },
            }}
        >
            <ArrowBack sx={{ fontSize: 16, color: retroColors.accent }} />
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                color: retroColors.accent,
                fontWeight: 500,
            }}>
                {label}
            </Typography>
        </Box>
    );
};

const RetroNavigation = ({ currentApp, scenarioTab, onScenarioTabChange, phdSection, onPhdSectionChange, evSection, onEvSectionChange, onEvScenarioTabChange, coldSection, onColdSectionChange, appConfig, isAdmin, isSuperAdmin }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile } = useAuth();
    const {
        evProjectType,
        evScenarioTab,
        setEvScenarioTab,
        phdSection: contextPhdSection,
        setPhdSection: contextSetPhdSection,
        coldSection: contextColdSection,
        setColdSection: contextSetColdSection,
        activeProject,
        setActiveProject,
        clearProjectScope,
    } = useRetroContext();

    // For enableApp API calls
    const { enableApp } = useUnifiedProjects(null);

    // Clearance denied notification state
    const [clearanceDenied, setClearanceDenied] = useState(false);
    const [deniedModule, setDeniedModule] = useState('');

    // Enable app dialog state (for project-scoped sidebar)
    const [enableAppDialog, setEnableAppDialog] = useState({ open: false, appKey: null });
    const [enablingApp, setEnablingApp] = useState(false);

    // Check if user has access to a specific product
    const hasProductAccess = (productKey) => {
        if (!productKey) return true; // No product requirement
        return userProfile?.products?.[productKey] === true;
    };

    // Handle navigation with access check
    const handleNavClick = (item) => {
        if (item.requiresProduct && !hasProductAccess(item.requiresProduct)) {
            setDeniedModule(item.label);
            setClearanceDenied(true);
            return;
        }

        // If clicking an app nav item without an active project, redirect to all-projects
        const isAppNavItem = APPS_NAV_ITEMS.some(nav => nav.id === item.id);
        if (isAppNavItem && !activeProject) {
            navigate('/all-projects');
            return;
        }

        // Clear evSection when navigating to Flux EV main page
        if (item.path === '/ev' && onEvSectionChange) {
            onEvSectionChange(null);
        }
        // Set default phdSection when navigating to PhD from top-level nav
        if (item.id === 'flux-phd' && contextSetPhdSection) {
            contextSetPhdSection('dashboard');
        }
        navigate(item.path);
    };

    // Detect if we're in the Site DNA scenario editor (not Flux EV)
    const isInScenarioEditor = location.pathname.startsWith('/projects/') && location.pathname.includes('/scenarios/');

    // Detect if we're in Flux EMS
    const isInFluxEms = location.pathname.startsWith('/ems');

    // Detect if we're in Flux PhD
    const isInFluxPhd = location.pathname.startsWith('/phd');

    // Detect if we're in Flux EV project (not dashboard)
    const isInFluxEvProject = location.pathname.startsWith('/ev/') && location.pathname !== '/ev/';

    // Detect if we're inside a Flux EV scenario editor (/ev/:projectId/scenarios/:scenarioId)
    const isInFluxEvScenario = /^\/ev\/[^/]+\/scenarios\/[^/]+/.test(location.pathname);

    // Detect if we're anywhere in Flux EV (dashboard or project)
    const isInFluxEv = location.pathname.startsWith('/ev');

    // Detect if we're in a Site DNA project (not the project list)
    const isInSiteDnaProject = location.pathname.startsWith('/projects/') && !isInScenarioEditor;
    const siteDnaProjectMatch = location.pathname.match(/^\/projects\/([^/]+)/);
    const siteDnaProjectId = siteDnaProjectMatch ? siteDnaProjectMatch[1] : null;

    // Detect if we're in Flux COLD project (not dashboard)
    const isInFluxColdProject = location.pathname.startsWith('/cold-storage/') && location.pathname !== '/cold-storage/';

    // Detect if we're in project overview (/all-projects/:projectId)
    const isInProjectOverview = /^\/all-projects\/[^/]+/.test(location.pathname);

    // Detect if we're in admin section
    const isInAdmin = location.pathname.startsWith('/admin');

    // Determine which app nav item is active based on path
    const getActiveAppId = () => {
        const path = location.pathname;
        if (path === '/' || path === '/dashboard') return 'dashboard';
        if (path.startsWith('/all-projects')) return 'all-projects';
        if (path.startsWith('/projects')) return 'site-dna';
        if (path.startsWith('/ev')) return 'flux-ev';
        if (path.startsWith('/cold-storage')) return 'cold-storage';
        if (path.startsWith('/ems') || path.startsWith('/flux')) return 'flux-ems';
        if (path.startsWith('/phd')) return 'flux-phd';
        if (path.startsWith('/admin')) return 'admin';
        return null;
    };

    const activeAppId = getActiveAppId();

    // Get active admin path for highlighting
    const getActiveAdminPath = () => {
        const path = location.pathname;
        // Exact match for /admin dashboard
        if (path === '/admin') return '/admin';
        // Find matching admin item
        const allAdminItems = [...ADMIN_ACCOUNT_ITEMS, ...ADMIN_PRODUCT_ITEMS, ...ADMIN_PLATFORM_ITEMS];
        const match = allAdminItems.find(item => path.startsWith(item.path) && item.path !== '/admin');
        return match?.path || null;
    };

    const activeAdminPath = getActiveAdminPath();

    // Render admin navigation items
    const renderAdminItems = (items, color = '#e74c3c') => (
        items.map((item) => (
            <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                isActive={activeAdminPath === item.path}
                onClick={() => navigate(item.path)}
                color={color}
                href={item.path}
            />
        ))
    );

    // Handle enabling a disabled app from project-scoped sidebar
    const handleEnableApp = async () => {
        const appKey = enableAppDialog.appKey;
        if (!appKey || !activeProject) return;

        setEnablingApp(true);
        try {
            const updatedProject = await enableApp(activeProject.id, appKey, {});
            // Update activeProject in context with new apps state
            setActiveProject(updatedProject);
            setEnableAppDialog({ open: false, appKey: null });
            // Navigate to the app
            const route = buildProjectRoute(appKey, activeProject.id);
            navigate(route);
        } catch (err) {
            console.error('Failed to enable app:', err);
        } finally {
            setEnablingApp(false);
        }
    };

    // Handle clicking an enabled app in project scope
    const handleProjectAppClick = (appKey) => {
        if (!activeProject) return;
        const route = buildProjectRoute(appKey, activeProject.id);
        navigate(route);
    };

    return (
        <Box>
            {/* Back to Dashboard button when in admin */}
            {isInAdmin && (
                <BackButton
                    onClick={() => navigate('/dashboard')}
                    label="Back to Dashboard"
                    href="/dashboard"
                />
            )}

            {/* PROJECT-SCOPED SIDEBAR — when activeProject is set */}
            {activeProject && !isInAdmin && (
                <>
                    {/* Back to Projects */}
                    <BackButton
                        onClick={() => {
                            clearProjectScope();
                            navigate('/all-projects');
                        }}
                        label="Back to Projects"
                        href="/all-projects"
                    />

                    {/* Project Info - clickable to go back to project overview */}
                    <Box
                        sx={{
                            px: 1, mb: 2, cursor: 'pointer',
                            borderRadius: 1,
                            '&:hover': { backgroundColor: 'rgba(178, 140, 80, 0.1)' },
                        }}
                        onClick={() => navigate(`/all-projects/${activeProject.id}`)}
                    >
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: '#b28c50',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            lineHeight: 1.3,
                            mb: 0.5,
                        }}>
                            {activeProject.name}
                        </Typography>
                        {activeProject.location?.address && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOn sx={{ fontSize: 12, color: retroColors.dimmed }} />
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.6rem',
                                    color: retroColors.dimmed,
                                    lineHeight: 1.3,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {activeProject.location.address}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Project Apps */}
                    <NavSection title="Apps">
                        {ALL_APP_KEYS.map((appKey) => {
                            const nav = APP_NAV_MAP[appKey];
                            const appConfig = activeProject.apps?.[appKey];
                            const isEnabled = Array.isArray(appConfig)
                                ? appConfig.some(s => s.enabled)
                                : !!appConfig?.enabled;

                            if (isEnabled) {
                                return (
                                    <NavItem
                                        key={appKey}
                                        icon={nav.icon}
                                        label={nav.label}
                                        isActive={activeAppId === nav.id}
                                        onClick={() => handleProjectAppClick(appKey)}
                                        color={nav.color}
                                        href={activeProject ? buildProjectRoute(appKey, activeProject.id) : undefined}
                                    />
                                );
                            }

                            return (
                                <DimmedNavItem
                                    key={appKey}
                                    icon={nav.icon}
                                    label={nav.label}
                                    color={nav.color}
                                    onClick={() => setEnableAppDialog({ open: true, appKey })}
                                />
                            );
                        })}
                    </NavSection>
                </>
            )}

            {/* STANDARD SIDEBAR — when no activeProject */}
            {!activeProject && !isInAdmin && (
                <>
                    <NavSection title="Navigation">
                        {PRIMARY_NAV_ITEMS
                            .filter((item) => !item.requiresProduct || hasProductAccess(item.requiresProduct))
                            .map((item) => (
                                <NavItem
                                    key={item.id}
                                    icon={item.icon}
                                    label={item.label}
                                    isActive={activeAppId === item.id}
                                    onClick={() => handleNavClick(item)}
                                    color={item.color}
                                    href={item.path}
                                />
                            ))}
                    </NavSection>

                    <NavSection title="Apps — Select a Project">
                        {APPS_NAV_ITEMS
                            .filter((item) => !item.requiresProduct || hasProductAccess(item.requiresProduct))
                            .map((item) => (
                                <DimmedNavItem
                                    key={item.id}
                                    icon={item.icon}
                                    label={item.label}
                                    color={item.color}
                                    onClick={() => handleNavClick(item)}
                                />
                            ))}
                    </NavSection>
                </>
            )}

            {/* Admin Navigation - show when in admin section */}
            {isInAdmin && isAdmin && (
                <>
                    <NavSection title="Account">
                        {renderAdminItems(ADMIN_ACCOUNT_ITEMS)}
                    </NavSection>

                    <NavSection title="Configuration">
                        {renderAdminItems(ADMIN_PRODUCT_ITEMS)}
                    </NavSection>

                    {isSuperAdmin && (
                        <NavSection title="Platform">
                            {renderAdminItems(ADMIN_PLATFORM_ITEMS)}
                        </NavSection>
                    )}
                </>
            )}

            {/* Site DNA Project Navigation — show when inside a project (not in scenario editor) */}
            {isInSiteDnaProject && siteDnaProjectId && (
                <NavSection title="Site DNA">
                    <NavItem
                        icon={Analytics}
                        label="Analysis"
                        isActive={location.pathname.includes('/analysis')}
                        onClick={() => navigate(`/projects/${siteDnaProjectId}/analysis`)}
                        color="#b28c50"
                        href={`/projects/${siteDnaProjectId}/analysis`}
                    />
                    <NavItem
                        icon={AcDcIcon}
                        label="AC/DC Systems"
                        isActive={location.pathname.includes('/acdc')}
                        onClick={() => navigate(`/projects/${siteDnaProjectId}/acdc`)}
                        color="#b28c50"
                        href={`/projects/${siteDnaProjectId}/acdc`}
                    />
                </NavSection>
            )}

            {/* Scenario Tabs - only show when in ScenarioEditor */}
            {isInScenarioEditor && (
                <NavSection title="Scenario">
                    {SCENARIO_TAB_ITEMS.map((item) => (
                        <NavItem
                            key={item.index}
                            icon={item.icon}
                            label={item.label}
                            isActive={scenarioTab === item.index}
                            onClick={() => onScenarioTabChange?.(item.index)}
                            color={appConfig?.color || '#b28c50'}
                            href={`${location.pathname}?tab=${item.index}`}
                        />
                    ))}
                </NavSection>
            )}

            {/* Flux EMS Navigation - only show when in Flux EMS */}
            {isInFluxEms && (() => {
                // Extract projectId from URL: /ems/:projectId/...
                const emsMatch = location.pathname.match(/^\/ems\/([^/]+)/);
                const emsProjectId = emsMatch ? emsMatch[1] : '';
                return (
                    <NavSection title="Flux EMS">
                        <NavItem
                            icon={ViewInArIcon}
                            label="Digital Twin"
                            isActive={true}
                            onClick={() => navigate(`/ems/${emsProjectId}`)}
                            color="#5fd4c4"
                            href={`/ems/${emsProjectId}`}
                        />
                    </NavSection>
                );
            })()}

            {/* Flux PhD Navigation - only show when in Flux PhD */}
            {isInFluxPhd && (
                <NavSection title="Flux PhD">
                    {FLUX_PHD_NAV_ITEMS.map((item) => (
                        <NavItem
                            key={item.id}
                            icon={item.icon}
                            label={item.label}
                            isActive={(contextPhdSection || phdSection) === item.id}
                            onClick={() => {
                                navigate(`/phd/${item.id}`);
                                if (contextSetPhdSection) {
                                    contextSetPhdSection(item.id);
                                }
                                onPhdSectionChange?.(item.id);
                            }}
                            color="#9b59b6"
                            href={`/phd/${item.id}`}
                        />
                    ))}
                </NavSection>
            )}

            {/* Flux EV Scenario Tabs — show when inside a scenario editor */}
            {isInFluxEvScenario && (() => {
                const evMatch = location.pathname.match(/^\/ev\/([^/]+)/);
                const evProjectId = evMatch ? evMatch[1] : '';
                return (
                    <>
                        <BackButton
                            onClick={() => navigate(`/ev/${evProjectId}/scenarios`)}
                            label="Back to Scenarios"
                            href={`/ev/${evProjectId}/scenarios`}
                        />
                        <NavSection title="Scenario">
                            {FLUX_EV_SCENARIO_TAB_ITEMS.map(item => (
                                <NavItem
                                    key={item.index}
                                    icon={item.icon}
                                    label={item.label}
                                    isActive={evScenarioTab === item.index}
                                    onClick={() => {
                                        setEvScenarioTab(item.index);
                                        onEvScenarioTabChange?.(item.index);
                                    }}
                                    color="#4CAF50"
                                    href={`${location.pathname}?tab=${item.index}`}
                                />
                            ))}
                        </NavSection>
                    </>
                );
            })()}

            {/* Flux EV Project Navigation - only show when inside a project (not in scenario editor) */}
            {isInFluxEvProject && !isInFluxEvScenario && evProjectType && (() => {
                // Extract projectId from URL: /ev/:projectId/...
                const evMatch = location.pathname.match(/^\/ev\/([^/]+)/);
                const evProjectId = evMatch ? evMatch[1] : '';
                // Extract current section from URL
                const evSectionMatch = location.pathname.match(/^\/ev\/[^/]+\/([^/]+)/);
                const urlEvSection = evSectionMatch ? evSectionMatch[1] : null;
                // Determine which nav items to show based on project type
                const projectNavItems = evProjectType === 'depot' ? DEPOT_NAV_ITEMS : FLUX_EV_NAV_ITEMS;
                const defaultEvSection = evProjectType === 'depot' ? 'upload' : 'scenarios';
                const activeEvSection = urlEvSection || evSection || defaultEvSection;

                return (
                    <NavSection title="Project">
                        {projectNavItems.map((item) => (
                            <NavItem
                                key={item.id}
                                icon={item.icon}
                                label={item.label}
                                isActive={activeEvSection === item.id}
                                onClick={() => navigate(`/ev/${evProjectId}/${item.id}`)}
                                color="#4CAF50"
                                href={`/ev/${evProjectId}/${item.id}`}
                            />
                        ))}
                    </NavSection>
                );
            })()}

            {/* Flux EV Tools - show on dashboard and inside projects, only if tools exist */}
            {isInFluxEv && FLUX_EV_TOOL_ITEMS.length > 0 && (
                <NavSection title="Tools">
                    {FLUX_EV_TOOL_ITEMS.map((item) => (
                        <NavItem
                            key={item.id}
                            icon={item.icon}
                            label={item.label}
                            isActive={evSection === item.id}
                            onClick={() => onEvSectionChange?.(item.id)}
                            color="#4CAF50"
                        />
                    ))}
                </NavSection>
            )}

            {/* Project Overview Navigation — when on /all-projects/:projectId */}
            {isInProjectOverview && activeProject && (
                <NavSection title="Project">
                    {PROJECT_OVERVIEW_NAV_ITEMS.map((item) => (
                        <NavItem
                            key={item.id}
                            icon={item.icon}
                            label={item.label}
                            isActive={location.hash === `#${item.id}` || (!location.hash && item.id === 'overview')}
                            onClick={() => {
                                navigate(`${location.pathname}#${item.id}`);
                            }}
                            color="#b28c50"
                            href={`${location.pathname}#${item.id}`}
                        />
                    ))}
                </NavSection>
            )}

            {/* Flux COLD Project Navigation - only show when inside a project */}
            {isInFluxColdProject && (() => {
                const coldProjectId = location.pathname.match(/^\/cold-storage\/([^/]+)/)?.[1];
                return (
                    <NavSection title="Project">
                        {FLUX_COLD_NAV_ITEMS.map((item) => (
                            <NavItem
                                key={item.id}
                                icon={item.icon}
                                label={item.label}
                                isActive={(contextColdSection || coldSection) === item.id}
                                onClick={() => {
                                    // Use context setter if available, otherwise fall back to prop
                                    if (contextSetColdSection) {
                                        contextSetColdSection(item.id);
                                    }
                                    onColdSectionChange?.(item.id);
                                }}
                                color="#2196F3"
                                href={coldProjectId ? `/cold-storage/${coldProjectId}?section=${item.id}` : undefined}
                            />
                        ))}
                    </NavSection>
                );
            })()}

            {/* Enable App Dialog (project-scoped sidebar) */}
            {enableAppDialog.appKey && (
                <Dialog
                    open={enableAppDialog.open}
                    onClose={() => !enablingApp && setEnableAppDialog({ open: false, appKey: null })}
                    PaperProps={{
                        sx: {
                            background: '#0a0a0a',
                            border: '1px solid rgba(178, 140, 80, 0.3)',
                            boxShadow: '0 0 30px rgba(178, 140, 80, 0.15)',
                            minWidth: 340,
                        }
                    }}
                >
                    <DialogTitle sx={{
                        fontFamily: vortxTypography.heading,
                        fontWeight: 700,
                        color: '#b28c50',
                        fontSize: '0.95rem',
                        borderBottom: '1px solid rgba(178, 140, 80, 0.2)',
                        pb: 2,
                    }}>
                        Add {APP_NAV_MAP[enableAppDialog.appKey]?.label} to Project?
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: '#aaa',
                            lineHeight: 1.6,
                        }}>
                            Enable <strong style={{ color: APP_NAV_MAP[enableAppDialog.appKey]?.color }}>
                                {APP_NAV_MAP[enableAppDialog.appKey]?.label}
                            </strong> for project <strong style={{ color: '#b28c50' }}>
                                {activeProject?.name}
                            </strong>?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{
                        borderTop: '1px solid rgba(178, 140, 80, 0.2)',
                        px: 3,
                        py: 2,
                    }}>
                        <Button
                            onClick={() => setEnableAppDialog({ open: false, appKey: null })}
                            disabled={enablingApp}
                            sx={{
                                color: retroColors.muted,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleEnableApp}
                            disabled={enablingApp}
                            variant="contained"
                            sx={{
                                background: 'linear-gradient(135deg, #b28c50 0%, #d4a574 100%)',
                                color: '#000',
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                px: 3,
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #d4a574 0%, #e5b88a 100%)',
                                },
                                '&.Mui-disabled': { opacity: 0.5 },
                            }}
                        >
                            {enablingApp ? 'Enabling...' : 'Enable'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Clearance Denied Notification */}
            <Snackbar
                open={clearanceDenied}
                autoHideDuration={3000}
                onClose={() => setClearanceDenied(false)}
                TransitionComponent={Slide}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Box
                    sx={{
                        bgcolor: 'rgba(10, 8, 8, 0.98)',
                        border: `1px solid ${vortxColors.error}`,
                        px: 4,
                        py: 2.5,
                        boxShadow: `0 0 30px ${vortxColors.error}66, inset 0 0 30px ${vortxColors.error}1a`,
                        textAlign: 'center',
                        fontFamily: vortxTypography.mono,
                        position: 'relative',
                        // Corner accents
                        '&::before, &::after': {
                            content: '""',
                            position: 'absolute',
                            width: '12px',
                            height: '12px',
                            borderColor: vortxColors.error,
                        },
                        '&::before': {
                            top: 4,
                            left: 4,
                            borderTop: '2px solid',
                            borderLeft: '2px solid',
                        },
                        '&::after': {
                            bottom: 4,
                            right: 4,
                            borderBottom: '2px solid',
                            borderRight: '2px solid',
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: vortxColors.error,
                            letterSpacing: '0.25em',
                            textTransform: 'uppercase',
                            textShadow: `0 0 15px ${vortxColors.error}cc`,
                            mb: 0.5,
                        }}
                    >
                        ⚠ CLEARANCE DENIED ⚠
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: `${vortxColors.error}b3`,
                            letterSpacing: '0.1em',
                        }}
                    >
                        {deniedModule} access not granted
                    </Typography>
                </Box>
            </Snackbar>
        </Box>
    );
};

export default RetroNavigation;
